import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getIdToken } from "firebase/auth";
import addToWalletBadge from "../../assets/img/add-to-apple-wallet.svg";
import { auth } from "../../firebase";
import { LoyaltyBusiness, LoyaltyInfo } from "../../contexts/CustomerContext";
import { formatPoints } from "../../utils/orders";
import { darken } from "./loyaltyHelpers";
import { formatLoyaltyAmount } from "../../utils/format";
import { sharedLocationsText } from "../../utils/loyaltyBusiness";
import styles from "./LoyaltyModal.module.scss";

const WALLET_PASS_URL =
  "https://europe-west1-sally-pos.cloudfunctions.net/v2-products-customerOrdering-location-walletPass";

// Hidden for now. A pass in Wallet only refreshes when the PassKit web service
// pushes it, so a customer who added one is left looking at a stale balance —
// better no card than a wrong one. Flip back to true once pass rotation is
// reliable; the issuing endpoint and the whole flow below still work.
const WALLET_PASS_ENABLED = false;

const isIOS =
  typeof navigator !== "undefined" &&
  /iPhone|iPad|iPod/.test(navigator.userAgent) &&
  !("MSStream" in window);

interface LoyaltyHeaderProps {
  locationId: string;
  locationName: string;
  /** Set when the loyalty is shared across this business's locations. */
  business: LoyaltyBusiness | null;
  loyalty: LoyaltyInfo;
  currency: string;
  onClose: () => void;
}

const LoyaltyHeader: React.FC<LoyaltyHeaderProps> = ({
  locationId,
  locationName,
  business,
  loyalty,
  currency,
  onClose,
}) => {
  const navigate = useNavigate();
  const [walletLoading, setWalletLoading] = useState(false);
  const { balance, spend, tier, settings } = loyalty;

  async function handleAddToWallet() {
    const user = auth.currentUser;
    if (!user) return;
    setWalletLoading(true);
    try {
      const token = await getIdToken(user);
      const res = await fetch(`${WALLET_PASS_URL}?locationId=${locationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error ?? `Server error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "loyalty.pkpass";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("[AddToWallet]", err);
      const isNetworkError = err instanceof TypeError;
      const message = isNetworkError
        ? "Could not reach the server. This may be a CORS or network issue — check the console."
        : (err?.message ?? "Could not generate your pass. Please try again.");
      alert(message);
    } finally {
      setWalletLoading(false);
    }
  }

  const curr = currency || "";
  const redeemRate: number = settings?.redeemRate ?? 0;
  const redeemableValue = redeemRate > 0 ? balance * redeemRate : null;
  const headerColor = tier?.color ?? "#000000";

  return (
    <div
      className={styles.Header}
      style={{
        background: `linear-gradient(150deg, ${headerColor} 0%, ${darken(headerColor, 24)} 100%)`,
      }}
    >
      {/* Close button */}
      <button
        className={styles.CloseBtn}
        onClick={onClose}
        type="button"
        aria-label="Close"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M1 1l12 12M13 1L1 13"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* Add to Apple Wallet — top-left, opposite the close button */}
      {WALLET_PASS_ENABLED && isIOS && (
        <button
          className={styles.WalletBtn}
          type="button"
          onClick={handleAddToWallet}
          disabled={walletLoading}
          aria-label="Add to Apple Wallet"
        >
          {walletLoading ? (
            <span className={styles.WalletBtnSpinner} />
          ) : (
            <img
              src={addToWalletBadge}
              alt="Add to Apple Wallet"
              className={styles.WalletBadge}
            />
          )}
        </button>
      )}

      <p className={styles.HeaderLocation}>
        {business
          ? `${business.name} · ${sharedLocationsText(business)}`
          : locationName}
      </p>

      {/* Points — horizontal */}
      <div className={styles.HeaderCore}>
        <p className={styles.HeaderPoints}>{formatPoints(balance)}</p>
        <p className={styles.HeaderPtsLabel}>pts</p>
      </div>

      {tier && (
        <div className={styles.TierBadge}>
          <span>★</span> {tier.name}
        </div>
      )}

      <div className={styles.StatsStrip}>
        <div className={styles.StatItem}>
          <span className={styles.StatItemValue}>
            {curr}
            {formatLoyaltyAmount(spend)}
          </span>
          <span className={styles.StatItemLabel}>
            {business
              ? `Total spend (${sharedLocationsText(business)})`
              : "Total spend"}
          </span>
        </div>
        {redeemableValue != null && (
          <>
            <div className={styles.StatStripDivider} />
            <div className={styles.StatItem}>
              <span className={styles.StatItemValue}>
                {curr}
                {formatLoyaltyAmount(redeemableValue)}
              </span>
              <span className={styles.StatItemLabel}>Redeemable value</span>
            </div>
          </>
        )}
        {settings?.pointsRate != null && (
          <>
            <div className={styles.StatStripDivider} />
            <div className={styles.StatItem}>
              <span className={styles.StatItemValue}>1 pt</span>
              <span className={styles.StatItemLabel}>
                per {curr}
                {Math.round(1 / settings.pointsRate)} spent
              </span>
            </div>
          </>
        )}
      </div>

      <button
        className={styles.HeaderHistoryLink}
        type="button"
        onClick={() => {
          onClose();
          navigate(`/location/${locationId}/points`);
        }}
      >
        View points history →
      </button>
    </div>
  );
};

export default LoyaltyHeader;
